<template>
  <div class="banner-wrapper">
    <!-- 首页轮播banner(可包含视频以及图片) -->
    <div class="swiper-container" id="mySwiper">
      <div class="swiper-wrapper">
        <div
            class="swiper-slide"
            v-for="(item, index) in bannerList"
            :key="index"
        >
          <!-- 视频轮播项 -->
          <div 
            v-if="item.type === 'video'" 
            class="slide-content video-slide"
            :class="{'has-link': item.href}"
            @click="handleBannerClick(item)"
          >
            <video class="banner-video" :src="item.url" autoplay muted playsinline></video>
            <div class="banner-overlay"></div>
            <div class="banner-content">
              <h2 class="banner-title">{{item.title}}</h2>
              <p class="banner-subtitle">{{item.subTitle}}</p>
            </div>
          </div>
          <!-- 图片轮播项 -->
          <div 
            v-else 
            class="slide-content image-slide"
            :class="{'has-link': item.href}"
            @click="handleBannerClick(item)"
          >
            <img class="banner-image" :src="item.url" alt="banner">
            <div class="banner-overlay"></div>
            <div class="banner-content">
              <h2 class="banner-title">{{item.title}}</h2>
              <p class="banner-subtitle">{{item.subTitle}}</p>
            </div>
          </div>
        </div>
      </div>
      <!-- 垂直分页器 -->
      <div class="vertical-pagination1" v-if="bannerList.length>1">
        <p
            class="swiper-pagination-bullet1"
            :class="{'swiper-pagination-bullet1-active': index === activeIndex}"
            v-for="(item, index) in bannerList"
            :key="index"
            @click="goToSlide(index)"
        ></p>
      </div>
    </div>
  </div>
</template>

<script>
import Swiper from 'swiper';

export default {
  name: 'HomeBanner',
  props: {
    bannerData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      bannerList: this.bannerData || [],
      swiper: null,
      autoplayDuration: 5000, // 默认轮播时间（毫秒）
      currentVideoIndex: null,
      activeIndex: 0,
      slideChangeHandler: null
    };
  },
  watch: {
    bannerData: {
      handler(newVal) {
        this.bannerList = Array.isArray(newVal) ? newVal : [];
        this.$nextTick(() => {
          this.initSwiper();
        });
      },
      immediate: true
    }
  },
  beforeDestroy() {
    if (this.swiper) {
      if (this.slideChangeHandler) {
        this.swiper.off('slideChangeTransitionEnd', this.slideChangeHandler);
        this.slideChangeHandler = null;
      }
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  },
  methods: {
    // 初始化swiper
    initSwiper() {
      if (this.bannerList.length === 0) return;

      if (this.swiper) {
        this.swiper.destroy(true, true);
      }

      const {autoplayDuration} = this;
      const enableLoop = this.bannerList.length > 1;
      const loopedSlides = enableLoop ? this.bannerList.length : 0;
      this.swiper = new Swiper('#mySwiper', {
        direction: 'horizontal',
        loop: enableLoop,
        loopedSlides,
        loopAdditionalSlides: loopedSlides,
        initialSlide: 0,
        autoplay: {
          delay: autoplayDuration,
          disableOnInteraction: false // 点击分页器后仍继续自动播放
        }
      });
      this.bindSwiperEvents();
      // this.handleSlideChange();
    },

    bindSwiperEvents() {
      if (!this.swiper) return;
      if (this.slideChangeHandler) {
        this.swiper.off('slideChangeTransitionEnd', this.slideChangeHandler);
      }
      this.slideChangeHandler = () => {
        console.log('轮播切换事件触发', this.swiper.realIndex)
        this.handleSlideChange();
      };
      this.swiper.on('slideChangeTransitionEnd', this.slideChangeHandler);
    },

    resumeAutoplay() {
      if (this.swiper && this.swiper.autoplay) {
        if (!this.swiper.autoplay.running) {
          this.swiper.autoplay.start();
        }
      }
    },

    pauseAllVideos() {
      if (!this.$el) return;
      const videos = this.$el.querySelectorAll('.banner-video');
      videos.forEach(video => {
        video.pause();
        video.currentTime = 0;
      });
    },

    // 处理轮播切换事件
    handleSlideChange() {
      if (!this.swiper) return;

      this.pauseAllVideos();

      const realIndex = this.swiper.realIndex;
      this.activeIndex = realIndex;
      const currentSlide = this.bannerList[realIndex];
      const activeSlide = this.swiper.slides[this.swiper.activeIndex];

      if (currentSlide && currentSlide.type === 'video') {
        const videoElement = activeSlide ? activeSlide.querySelector('video') : null;
        if (videoElement) {
          this.currentVideoIndex = realIndex;
          videoElement.currentTime = 0;
          this.swiper.autoplay.stop();

          const playPromise = videoElement.play();
          videoElement.onended = () => {
            videoElement.onended = null;
            this.currentVideoIndex = null;
            if (this.swiper && !this.swiper.destroyed) {
              this.swiper.slideNext();
            }
          };

          if (playPromise && playPromise.catch) {
            playPromise.catch((e) => {
              console.warn('视频播放被中断:', e);
              this.currentVideoIndex = null;
              this.resumeAutoplay();
            });
          }
        }
      } else {
        this.currentVideoIndex = null;
        this.resumeAutoplay();
      }
    },

    goToSlide(index) {
      if (!this.swiper) return;
      if (this.swiper.params.loop) {
        this.swiper.slideToLoop(index);
      } else {
        this.swiper.slideTo(index);
      }
    },

    // 跳转页面
    handleBannerClick(item) {
      if (item && item.href) {
        window.open(item.href, '_blank');
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import '../assets/styles/variables.less';
@import '../assets/styles/mixins.less';

.banner-wrapper {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;

  .swiper-container {
    width: 100%;
    height: 100%;
  }

  .swiper-slide {
    width: 100%;
    height: 100%;

    .slide-content {
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;

      &.has-link {
        cursor: pointer;
      }
    }

    .banner-video,
    .banner-image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .banner-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.2);
      z-index: 2;
    }

    .banner-content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 3;
      color: #fff;
      text-align: center;
      width: 80%;
      opacity: 0;
      transition: opacity 0.6s ease;

      .banner-title {
        font-size: 75px;
        font-weight: bold;
        margin-bottom: 40px;
        opacity: 0;
        transform: translateY(30px);
        transition: none;

        @media (max-width: @screen-md) {
          font-size: 36px;
        }

        @media (max-width: @screen-sm) {
          font-size: 28px;
        }
      }

      .banner-subtitle {
        font-size: 32px;
        font-weight: 300;
        opacity: 0;
        transform: translateY(30px);
        transition: none;

        @media (max-width: @screen-md) {
          font-size: @font-size-note;
        }

        @media (max-width: @screen-sm) {
          font-size: 16px;
        }
      }
    }

    &.swiper-slide-active {
      .banner-content {
        opacity: 1;
      }

      .banner-title {
        animation: bannerFadeUp 0.8s ease forwards;
      }

      .banner-subtitle {
        animation: bannerFadeUp 0.8s ease 0.15s forwards;
      }
    }
  }

  // 垂直分页器样式
  :deep(.vertical-pagination1) {
    position: absolute;
    left: 45px;
    top: calc(50% + 35px);
    transform: translateY(-50%);
    width: 60px !important;
    height: fit-content;
    min-height: 120px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 0;
    background: rgba(255, 255, 255, 0.25);
    border-radius: 30px;
    z-index: 10;

    .swiper-pagination-bullet1 {
      display: inline !important;
      margin: 10px 0 !important;
      width: 15px !important;
      height: 15px !important;
      background: rgba(255, 255, 255, 0.5) !important;
      opacity: 1 !important;
      border-radius: 50% !important;
      border: none !important;
    }

    .swiper-pagination-bullet1-active {
      width: 16px !important;
      height: 16px !important;
      background: transparent !important;
      border: 2px solid #fff !important;
      opacity: 1 !important;
    }
  }
}

@keyframes bannerFadeUp {
  0% {
    opacity: 0;
    transform: translateY(40px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>